<template>
  <div id="layout">
    <MenuSuperiorComponent />
    <div id="content">
      <div
        id="alertas"
        v-if="mostrarNotificacao"
        role="status"
        aria-live="polite"
        @click="redirecionamento"
      >
        <div class="notificacao-card">
          <div class="notificacao-icone" aria-hidden="true">?</div>
          <div class="notificacao-corpo">
            <p class="notificacao-titulo">{{ notificacaoAtual?.titulo }}</p>
            <p class="notificacao-texto">{{ notificacaoAtual?.conteudo }}</p>
            <span class="notificacao-acao">Completar perfil →</span>
          </div>
          <button
            type="button"
            class="notificacao-fechar"
            aria-label="Fechar aviso"
            @click.stop="mostrarNotificacao = false"
          >
            ✕
          </button>
        </div>
      </div>
      <AjudaComponent />
      <router-view :key="$route.params.id || $route.name" />

      <!-- Modal Bloqueante de Consentimento dos Termos de Uso e Privacidade -->
      <ModalTermoConsentimento
        :visivel="mostrarModalTermo"
        :termo="termoVigente"
        @concordou="onTermoConcordado"
        @cancelou="mostrarModalTermo = false"
      />
    </div>
    <FooterComponent />
  </div>
</template>

<script>
import MenuSuperiorComponent from "@/components/layout/MenuSuperiorComponent.vue";
import FooterComponent from "@/components/layout/FooterComponent.vue";
import {
  iniciarTimerRenovacaoToken,
  pararTimer,
} from "@/utils/timerTokenRefresh";
import { getRefreshToken } from "@/utils/tokenService";
import usuariosService from "@/service/usuariosService";
import termoService from "@/service/termoService";
import AjudaIconComponent from "@/components/common/AjudaIconComponent.vue";
import ModalTermoConsentimento from "@/components/modals/ModalTermoConsentimento.vue";
import { useAuthStore } from "@/stores/auth";

export default {
  data() {
    return {
      mostrarNotificacao: false,
      notificacoes: {
        notificacaoAtualizarPerfil: {
          titulo: "Cadastro incompleto",
          conteudo:
            "Faltam algumas informações no seu perfil. Atualize quando puder pelo ícone do seu perfil.",
          urlRedirecionamento: "/perfil",
        },
      },
      notificacaoAtual: null,
      mostrarModalTermo: false,
      termoVigente: null,
    };
  },
  components: {
    MenuSuperiorComponent,
    FooterComponent,
    AjudaComponent: AjudaIconComponent,
    ModalTermoConsentimento,
  },
  async mounted() {
    this.gerenciarSessao();
    const refreshToken = getRefreshToken();
    if (refreshToken && this.$route.path !== "/login") {
      await this.getNotificacao();
      await this.verificarTermos();
    }
  },
  beforeUnmount() {
    pararTimer(); // Limpa ao destruir o componente
  },
  methods: {
    gerenciarSessao() {
      const refreshToken = getRefreshToken();
      if (refreshToken && this.$route.path !== "/login") {
        iniciarTimerRenovacaoToken();
      }
    },
    redirecionamento() {
      this.$router.push(this.notificacaoAtual.urlRedirecionamento);
      this.mostrarNotificacao = false;
    },
    async getNotificacao() {
      const authStore = useAuthStore();
      try {
        const response = await usuariosService.notificacaoAtualizarPerfil();
        authStore.perfilIncompleto = !!response;

        if (response) {
          const jaNotificadoNoLogin = window.localStorage.getItem(
            "perfil_incompleto_notificado"
          );
          if (!jaNotificadoNoLogin) {
            this.notificacaoAtual = {
              ...this.notificacoes.notificacaoAtualizarPerfil,
            };
            this.mostrarNotificacao = true;
            window.localStorage.setItem("perfil_incompleto_notificado", "true");
          }
        } else {
          this.mostrarNotificacao = false;
        }
      } catch (e) {
        authStore.perfilIncompleto = false;
      }
    },
    async verificarTermos() {
      const authStore = useAuthStore();
      if (!authStore.isAutenticado) return;

      try {
        const termo = await termoService.obterTermoVigente();
        if (termo) {
          this.termoVigente = termo;
          const precisa = Boolean(termo.precisaConcordar);
          authStore.definirStatusTermo(precisa, termo);
          this.mostrarModalTermo = precisa;
        }
      } catch (e) {
        console.error("Erro ao verificar termo vigente no layout:", e);
      }
    },
    onTermoConcordado(dados) {
      this.mostrarModalTermo = false;
      const authStore = useAuthStore();
      authStore.definirStatusTermo(false, {
        ...this.termoVigente,
        usuarioConcordou: true,
        usuarioVersaoConcordada: this.termoVigente?.versao,
        usuarioDataConcordancia: dados?.dataConcordancia || new Date().toISOString(),
        precisaConcordar: false,
      });
    },
  },
  watch: {
    // Caso a rota mude para login, para o timer
    "$route.path"(newPath) {
      if (newPath === "/login") {
        pararTimer();
      } else if (newPath === "/perfil") {
        this.mostrarNotificacao = false;
      } else {
        const authStore = useAuthStore();
        if (authStore.isAutenticado && !this.termoVigente) {
          this.verificarTermos();
        }
      }
    },
  },
};
</script>

<style scoped>
#layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

#content {
  flex: 1;
  min-width: 0;
  min-height: calc(100vh - 2rem);
  padding: 1rem 2rem;
  background-color: var(--branco);
}

#alertas {
  position: fixed;
  top: 102px;
  right: 24px;
  width: min(360px, calc(100vw - 32px));
  z-index: 9999;
  cursor: pointer;
}

.notificacao-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: hsl(0, 0%, 100%);
  border: 1px solid hsl(32, 85%, 82%);
  border-left: 4px solid hsl(32, 95%, 52%);
  border-radius: 12px;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notificacao-card::before {
  content: "";
  position: absolute;
  top: -7px;
  right: 16px;
  width: 12px;
  height: 12px;
  background-color: hsl(0, 0%, 100%);
  border-top: 1px solid hsl(32, 85%, 82%);
  border-left: 1px solid hsl(32, 85%, 82%);
  transform: rotate(45deg);
}

.notificacao-card:hover {
  transform: translateY(-2px);
  border-color: hsl(32, 95%, 52%);
}

.notificacao-icone {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: hsl(32, 95%, 52%);
  color: hsl(0, 0%, 100%);
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.notificacao-corpo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.notificacao-titulo {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: hsl(258, 35%, 18%);
  line-height: 1.3;
}

.notificacao-texto {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: hsl(258, 12%, 40%);
  line-height: 1.4;
}

.notificacao-acao {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
  color: var(--roxo-primario);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notificacao-card:hover .notificacao-acao {
  color: var(--roxo-medio);
}

.notificacao-fechar {
  background: transparent;
  border: none;
  color: hsl(258, 12%, 52%);
  font-size: 14px;
  line-height: 1;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.notificacao-fechar:hover {
  background-color: hsl(258, 20%, 94%);
  color: hsl(258, 35%, 18%);
}

@media (max-width: 768px) {
  #content {
    padding: 0.5rem 0.8rem;
  }

  #alertas {
    top: 82px;
    right: 16px;
    width: calc(100vw - 32px);
  }
}
</style>
